import type { Json, Tables } from "@/integrations/supabase/types";

type AdminActionRecord = Pick<Tables<"admin_actions">, "action" | "metadata">;

export type AuditField = {
  label: string;
  value: string;
};

export type FormattedAuditEntry = {
  title: string;
  detail?: string;
  fields: AuditField[];
};

export type AuditLogEntry = Pick<Tables<"admin_actions">, "id" | "action" | "created_at" | "metadata"> & {
  formatted: FormattedAuditEntry;
  actorLabel?: string | null;
  targetLabel?: string | null;
};

const isJsonObject = (value: Json | null | undefined): value is Record<string, Json | undefined> => {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const formatLabel = (value: string) => value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value: Json | undefined): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return JSON.stringify(value);
};

const field = (label: string, value: Json | undefined): AuditField | null => {
  const formatted = formatValue(value);
  if (!formatted || formatted === "—") return null;
  return { label, value: formatted };
};

const compactFields = (...values: Array<AuditField | null>): AuditField[] => values.filter(Boolean) as AuditField[];

const fallbackFields = (metadata: Json | null): AuditField[] => {
  if (!isJsonObject(metadata)) return [];

  return Object.entries(metadata)
    .map(([key, value]) => field(formatLabel(key), value))
    .filter(Boolean) as AuditField[];
};

export const formatAdminAuditEntry = ({ action, metadata }: AdminActionRecord): FormattedAuditEntry => {
  const meta = isJsonObject(metadata) ? metadata : null;
  const key = meta?.key;
  const name = meta?.name;
  const targetLabel = meta?.target_label;
  const targetType = meta?.target_type;
  const enabled = meta?.enabled;
  const rolloutMode = meta?.rollout_mode;

  switch (action) {
    case "feature_flag_created":
      return {
        title: `Created feature flag ${formatValue(key)}`,
        detail: "New flag is available for rollout configuration.",
        fields: compactFields(field("Key", key), field("Flag ID", meta?.feature_flag_id)),
      };
    case "feature_flag_toggle_changed":
      return {
        title: `${enabled ? "Enabled" : "Disabled"} ${formatValue(key)}`,
        detail: enabled ? "The master switch is on." : "The master switch is off.",
        fields: compactFields(field("Status", enabled), field("Rollout", rolloutMode)),
      };
    case "feature_flag_rollout_changed":
      return {
        title: `Changed rollout for ${formatValue(key)}`,
        detail: rolloutMode ? `Now using ${formatValue(rolloutMode)} mode.` : undefined,
        fields: compactFields(field("Rollout", rolloutMode), field("Status", enabled)),
      };
    case "feature_flag_metadata_updated":
      return {
        title: `Updated details for ${formatValue(key)}`,
        detail: "Flag copy or description was edited.",
        fields: compactFields(field("Name", name), field("Description", meta?.description)),
      };
    case "feature_flag_target_added":
      return {
        title: `Added ${formatValue(targetLabel)} to ${formatValue(key)}`,
        detail: "Allowlist expanded.",
        fields: compactFields(field("Target type", targetType), field("Target", targetLabel)),
      };
    case "feature_flag_target_removed":
      return {
        title: `Removed ${formatValue(targetLabel)} from ${formatValue(key)}`,
        detail: "Allowlist reduced.",
        fields: compactFields(field("Target type", targetType), field("Target", targetLabel)),
      };
    case "create_workspace":
      return {
        title: `Created workspace ${formatValue(meta?.label)}`,
        fields: compactFields(field("Workspace", meta?.label)),
      };
    case "rename_workspace":
      return {
        title: `Renamed workspace to ${formatValue(meta?.label)}`,
        fields: compactFields(field("Workspace", meta?.label)),
      };
    case "delete_workspace":
      return {
        title: `Deleted workspace ${formatValue(meta?.label)}`,
        fields: compactFields(field("Workspace", meta?.label)),
      };
    case "change_tier":
      return {
        title: `Changed tier for ${formatValue(meta?.label)}`,
        detail: meta?.tier ? `Now on ${formatValue(meta.tier)}.` : undefined,
        fields: compactFields(field("Workspace", meta?.label), field("Tier", meta?.tier)),
      };
    default:
      return {
        title: formatLabel(action),
        detail: meta ? "Recorded with structured metadata." : undefined,
        fields: fallbackFields(metadata),
      };
  }
};

export const getFeatureFlagSummary = ({
  enabled,
  rolloutMode,
  userCount,
  workspaceCount,
}: {
  enabled: boolean;
  rolloutMode: "off" | "admins" | "allowlist" | "all";
  userCount: number;
  workspaceCount: number;
}) => {
  if (!enabled) return "Disabled everywhere";
  if (rolloutMode === "all") return "Enabled for every signed-in user";
  if (rolloutMode === "admins") return "Enabled for admins only";
  return `Allowlist active • ${userCount} user${userCount === 1 ? "" : "s"} • ${workspaceCount} workspace${workspaceCount === 1 ? "" : "s"}`;
};

const normalizeSearchText = (value: string) => value.trim().toLowerCase();

const getSearchableAuditText = (entry: AuditLogEntry) => {
  const metadata = entry.metadata ? JSON.stringify(entry.metadata) : "";
  const fields = entry.formatted.fields.map((item) => `${item.label} ${item.value}`).join(" ");

  return normalizeSearchText([
    entry.action,
    entry.formatted.title,
    entry.formatted.detail ?? "",
    fields,
    metadata,
    entry.created_at,
    entry.actorLabel ?? "",
    entry.targetLabel ?? "",
  ].join(" "));
};

export const filterAuditEntries = (entries: AuditLogEntry[], query: string) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return entries;

  return entries.filter((entry) => getSearchableAuditText(entry).includes(normalizedQuery));
};

const escapeCsvCell = (value: string) => {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${safe.replace(/"/g, '""')}"`;
};

export const exportAuditEntriesToCsv = (entries: AuditLogEntry[]) => {
  const header = ["Timestamp", "Action", "Title", "Detail", "Actor", "Target", "Fields"];
  const rows = entries.map((entry) => [
    entry.created_at,
    entry.action,
    entry.formatted.title,
    entry.formatted.detail ?? "",
    entry.actorLabel ?? "",
    entry.targetLabel ?? "",
    entry.formatted.fields.map((item) => `${item.label}: ${item.value}`).join(" | "),
  ]);

  return [header, ...rows].map((row) => row.map((value) => escapeCsvCell(value)).join(",")).join("\n");
};